import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Drawer, styled, Typography, useTheme } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { CollapsibleSection, LwButton, MultiDateCalendar } from 'redesign';
import { useCalendarDefaults } from 'shared/hooks/useCalendarDefaults';
import { dateTimeToDateObject } from 'shared/components/shift-forms/components/shift-multiply-calendar/multi-date-object-utils';
import { useState } from 'react';
import { IconCaretLeft } from 'assets/img';
import { TimeSlotWrapper } from './time-slot-wrapper';
import { AvailabilityWorkersSelect } from './availability-workers-select';
import { SegmentsForEdit, TimeSlotFormContext } from './time-slot.types';
import { useHandleAvailabilitySubmission } from '../hooks/use-handle-availability-submission';
import { AvailabilityMode } from '../hooks/use-availability-action-handler';

export type AvailabilityForEdit = {
  selectedWorker: { id: string; name: string; description: string };
  availabilitySegments: SegmentsForEdit[];
  availabilityDate: DateTime;
};
export type SelectedDateWithAvailability = {
  date: DateTime;
  errored?: boolean;
  relatedAvailability?: { segmentId: string; startTime: DateTime; endTime: DateTime }[];
};

export const EditAvailabilitySidebar = ({
  availabilityForEdit,
  onClose,
  isOpen,
  fromDate,
  searchTerm,
  mode = 'edit',
}: {
  availabilityForEdit: AvailabilityForEdit | null;
  onClose: () => void;
  isOpen: boolean;
  fromDate: DateTime;
  searchTerm: string | undefined;
  mode?: AvailabilityMode;
}) => {
  const isCreateMode = mode === 'create';
  const { t } = useTranslation('all-platforms-common');
  const theme = useTheme();
  const { monthLabels, dayLabels } = useCalendarDefaults();
  const [selectedDate, setSelectedDate] = useState<DateTime>(
    isCreateMode ? fromDate : (availabilityForEdit?.availabilityDate ?? fromDate)
  );

  const [selectedFirstMonth, setSelectedFirstMonth] = useState<DateTime>(fromDate.startOf('month'));

  const availabilityDate = isCreateMode
    ? selectedDate.set({ hour: 12 })
    : (availabilityForEdit?.availabilityDate ?? fromDate);
  const selectedWorker = availabilityForEdit?.selectedWorker;
  const availabilitySegments = availabilityForEdit?.availabilitySegments ?? [];

  const formMethods = useForm<TimeSlotFormContext>({
    defaultValues: {
      workerSelect: isCreateMode ? '' : (selectedWorker?.id ?? ''),
      availability: isCreateMode
        ? [{ segmentId: '', utcStartDateTime: '', utcEndDateTime: '' }]
        : availabilitySegments.length > 0
          ? availabilitySegments
          : [],
    },
  });

  const { handleDeleteAll, handleSubmit } = useHandleAvailabilitySubmission({
    workerId: selectedWorker?.id,
    formMethods,
    originalAvailability: isCreateMode ? [] : availabilitySegments,
    availabilityDate,
    fromDate,
    searchTerm,
    cancel: onClose,
    mode,
  });

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose} PaperProps={{ style: { width: 600 } }}>
      <FormWrapper>
        <Box>
          <Typography variant="h1" mb={4}>
            {isCreateMode
              ? t('edit_availability.create_title') || 'Create Availability'
              : t('edit_availability.title')}
          </Typography>
        </Box>
        <Divider />
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit} className="formhtml">
            <FormBody>
              <CollapsibleSection
                title={t('edit_availability.worker_section_title')}
                initializeAsOpen={isCreateMode}
              >
                <AvailabilityWorkersSelect
                  workerOptions={selectedWorker ? [selectedWorker] : []}
                  disabled={!isCreateMode}
                />
              </CollapsibleSection>
              <CollapsibleSection title={t('edit_availability.availability_section_title')}>
                <CalendarWrapper data-testid="edit-availability-calendar">
                  <MultiDateCalendar
                    value={dateTimeToDateObject(selectedFirstMonth)}
                    currentDate={dateTimeToDateObject(selectedFirstMonth)}
                    onChange={(selectedDate) => {
                      if (isCreateMode && selectedDate) {
                        const dateObject = Array.isArray(selectedDate)
                          ? selectedDate[0]
                          : selectedDate;
                        if (dateObject && dateObject.toDate) {
                          const newDate = DateTime.fromJSDate(dateObject.toDate());
                          setSelectedDate(newDate);
                          formMethods.setValue('availability', [
                            { segmentId: '', utcStartDateTime: '', utcEndDateTime: '' },
                          ]);
                        }
                      }
                    }}
                    onMonthChange={(newMonth) => {
                      setSelectedFirstMonth(
                        DateTime.fromJSDate(newMonth.toDate()).startOf('month')
                      );
                    }}
                    mapDays={({ date, isSameDate }) => {
                      const isSelected = isSameDate(dateTimeToDateObject(availabilityDate), date);
                      return {
                        children: date.format('DD'),
                        disabled: !isCreateMode,
                        style: {
                          backgroundColor: isSelected
                            ? theme.palette.lwPrimaryAccentLight[100]
                            : 'inherit',
                          color: isSelected ? 'white' : 'inherit',
                        },
                      };
                    }}
                    weekStartDayIndex={1}
                    multiple={false}
                    buttons={isCreateMode}
                    renderButton={CustomButtons}
                    disableMonthPicker={!isCreateMode}
                    disableYearPicker={!isCreateMode}
                    months={monthLabels}
                    weekDays={dayLabels}
                    numberOfMonths={2}
                  />
                </CalendarWrapper>
                <SectionDivider />
                <TimeSlotWrapper isCreateMode={isCreateMode} additionalAvailabilities={[]} />
              </CollapsibleSection>
              <FormActionContainer>
                {!isCreateMode && (
                  <LwButton
                    onClick={handleDeleteAll}
                    color="secondary"
                    title={t('edit_availability.delete_all')}
                  >
                    {t('edit_availability.delete_all')}
                  </LwButton>
                )}
                {isCreateMode && <Box />}
                <StandardActionContainer>
                  <LwButton
                    onClick={onClose}
                    color="secondary"
                    title={t('edit_availability.close')}
                  >
                    {t('edit_availability.close')}
                  </LwButton>
                  <LwButton
                    color="primary"
                    type="submit"
                    title={
                      isCreateMode
                        ? t('edit_availability.create') || t('edit_availability.save')
                        : t('edit_availability.save')
                    }
                  >
                    {isCreateMode
                      ? t('edit_availability.create') || t('edit_availability.save')
                      : t('edit_availability.save')}
                  </LwButton>
                </StandardActionContainer>
              </FormActionContainer>
            </FormBody>
          </form>
        </FormProvider>
      </FormWrapper>
    </Drawer>
  );
};

const FormWrapper = styled(Box)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(8),
  overflow: 'auto',
  '& .formhtml': {
    height: '95%',
  },
}));
const StandardActionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));
const FormBody = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  width: '550px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

const FormActionContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignContent: 'center',
  marginTop: 'auto',
}));

const SectionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const CalendarWrapper = styled(Box)(({ theme }) => ({
  '&& .rmdp-week-day': {
    width: theme.spacing(8),
    height: theme.spacing(4),
  },

  '&& .rmdp-day': {
    width: theme.spacing(8),
    height: theme.spacing(8),
    margin: '2px',
  },

  '&& .rmdp-header': {
    height: 'auto',
    width: '100%',
    justifyContent: 'space-between',
    '& .rmdp-header-values': {
      padding: theme.spacing(2),
      '& > span': {
        paddingLeft: theme.spacing(2),
      },
    },
  },
}));

const CustomButtons = (
  direction: string,
  handleClick: React.MouseEventHandler<HTMLButtonElement> | undefined
) => {
  return (
    <StyledArrowButton onClick={handleClick}>
      <StyledArrow style={direction === 'right' ? { transform: 'rotate(180deg)' } : {}} />
    </StyledArrowButton>
  );
};

const StyledArrow = styled(IconCaretLeft)(() => ({
  height: '27px',
  width: '27px',
}));

const StyledArrowButton = styled('button')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  height: '39px',
  width: '39px',
  background: 'inherit',
  border: 'none',
  borderRadius: 8,
  padding: 0,

  '&:not(:disabled):hover': {
    background: theme.palette.lwPrimaryAccentLight[10],
    cursor: 'pointer',
    '& svg': {
      '& path': { stroke: theme.palette.lwSecondaryAccentLight[100] },
    },
  },
}));
